<script setup lang="ts">
import FeatureCard from "@/components/landing/features-card.vue";
import {
  BadgeCheck,
  Globe,
  HeartIcon,
  Wallet,
  TrendingDown,
  Shield,
} from "lucide-vue-next";
import Map from "@/components/ui/Map.vue";

const costBenefits = [
  {
    icon: TrendingDown,
    title: "Save up to 90%",
    description: "Compared to traditional roaming charges",
  },
  {
    icon: Wallet,
    title: "No Hidden Fees",
    description: "Pay only for what you use",
  },
  {
    icon: Shield,
    title: "Transparent Pricing",
    description: "Know your costs upfront",
  },
];
</script>

<template>
  <section class="bg-zinc-50 py-16 md:py-32 dark:bg-transparent">
    <div class="mb-12 text-center">
      <p class="text-lg text-zinc-400 mb-2">Why choose Jittr?</p>
      <h2 class="text-4xl md:text-5xl font-bold text-white">
        Stay connected while traveling
      </h2>
    </div>
    <div class="mx-auto max-w-2xl px-6 lg:max-w-5xl">
      <div class="mx-auto grid gap-4 lg:grid-cols-2">
        <!-- Feature Card 1: Easy to use -->
        <FeatureCard
          :icon="BadgeCheck"
          title="Easy to use"
          description="Install, Buy and Activate"
        >
          <div class="relative border-t border-dashed max-sm:mb-6">
            <div
              aria-hidden="true"
              class="absolute inset-0 [background:radial-gradient(125%_125%_at_50%_0%,transparent_40%,var(--color-blue-600),var(--color-white)_100%)]"
            />
            <div class="aspect-76/59 p-1 px-6"></div>
          </div>
        </FeatureCard>

        <!-- Feature Card 2: Global Coverage -->
        <FeatureCard
          :icon="Globe"
          title="Global Coverage"
          description="Jittr is available in over 200 countries and regions."
        >
          <div aria-hidden class="relative mt-20">
            <div class="absolute inset-0 z-10 m-auto size-fit">
              <div
                class="rounded-(--radius) bg-background z-1 dark:bg-muted relative flex size-fit w-fit items-center gap-2 border px-3 py-1 text-xs font-medium shadow-md shadow-zinc-950/5"
              >
                <HeartIcon class="size-5 text-red-500 animate-pulse" />
                Stay Connected to your loved ones
              </div>
            </div>
            <div
              class="absolute inset-0 z-0"
              :style="{
                backgroundImage:
                  'radial-gradient(125% 125% at 50% 90%, transparent 40%, #09090b 100%)',
                backgroundSize: '100% 100%',
              }"
            />
            <Map class="object-fit" />
          </div>
        </FeatureCard>

        <!-- Feature Card 3: Avoid roaming charges -->
        <FeatureCard
          variant="centered"
          description="Avoid roaming charges when you travel."
          className="lg:col-span-2"
        >
          <div
            class="grid grid-cols-1 md:grid-cols-3 gap-8 px-4 pb-6 max-w-4xl mx-auto"
          >
            <div
              v-for="(benefit, idx) in costBenefits"
              :key="idx"
              class="flex flex-col items-center text-center"
            >
              <component :is="benefit.icon" class="w-8 h-8 text-primary mb-4" />
              <h3 class="text-lg font-semibold text-white mb-2">
                {{ benefit.title }}
              </h3>
              <p class="text-sm text-zinc-400">{{ benefit.description }}</p>
            </div>
          </div>
        </FeatureCard>
      </div>
    </div>
  </section>
</template>
